import { createLogRecord, writeLog } from '@skiphr/logging'
import {
  GoogleAuthProvider,
  browserLocalPersistence,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendEmailVerification,
  signInWithEmailAndPassword,
  getRedirectResult,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  setPersistence,
  updateProfile,
  type User,
} from 'firebase/auth'
import { httpsCallable } from 'firebase/functions'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { firebaseClient, sameOriginAuth } from '@/firebase'

const toMessage = (error: unknown): string =>
  error instanceof Error ? error.message.replace('Firebase: ', '') : 'Something went wrong.'

export const useAuthStore = defineStore('authStore', () => {
  const user = ref<User | null>(null)
  const loading = ref(false)
  const initialized = ref(false)
  const error = ref('')
  const accountStatus = ref('')

  const isAuthenticated = computed(() => user.value !== null)
  const displayName = computed(
    () => user.value?.displayName ?? user.value?.email?.split('@')[0] ?? 'Operator',
  )

  const createAccountProfile = async (displayName: string | null) => {
    const createAccount = httpsCallable(firebaseClient.functions, 'createAccountProfile')
    await createAccount({
      displayName: displayName ?? 'Operator',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    })
  }

  async function initialize(): Promise<void> {
    if (initialized.value) return Promise.resolve()
    await setPersistence(firebaseClient.auth, browserLocalPersistence)
    if (sameOriginAuth) {
      // Completes a Google sign-in that returned from a full-page redirect.
      try {
        const credential = await getRedirectResult(firebaseClient.auth)
        if (credential) await createAccountProfile(credential.user.displayName)
      } catch (caught) {
        error.value = toMessage(caught)
      }
    }
    return new Promise((resolve) => {
      const stop = onAuthStateChanged(firebaseClient.auth, (nextUser) => {
        user.value = nextUser
        initialized.value = true
        stop()
        resolve()
      })
    })
  }

  async function register(name: string, email: string, password: string): Promise<void> {
    loading.value = true
    error.value = ''
    try {
      const credential = await createUserWithEmailAndPassword(firebaseClient.auth, email, password)
      await updateProfile(credential.user, { displayName: name })
      await sendEmailVerification(credential.user)
      const createAccount = httpsCallable(firebaseClient.functions, 'createAccountProfile')
      await createAccount({
        displayName: name,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      })
      user.value = credential.user
    } catch (caught) {
      error.value = toMessage(caught)
      writeLog(createLogRecord('warn', 'auth.register_failed', 'Registration failed'))
      throw caught
    } finally {
      loading.value = false
    }
  }

  async function login(email: string, password: string): Promise<void> {
    loading.value = true
    error.value = ''
    try {
      const credential = await signInWithEmailAndPassword(firebaseClient.auth, email, password)
      user.value = credential.user
    } catch (caught) {
      error.value = toMessage(caught)
      throw caught
    } finally {
      loading.value = false
    }
  }

  async function continueWithGoogle(): Promise<void> {
    loading.value = true
    error.value = ''
    try {
      // The page navigates away and initialize() finishes the sign-in on return.
      if (sameOriginAuth) return await signInWithRedirect(firebaseClient.auth, new GoogleAuthProvider())
      const credential = await signInWithPopup(firebaseClient.auth, new GoogleAuthProvider())
      await createAccountProfile(credential.user.displayName)
      user.value = credential.user
    } catch (caught) {
      error.value = toMessage(caught)
      throw caught
    } finally {
      loading.value = false
    }
  }

  async function logout(): Promise<void> {
    await signOut(firebaseClient.auth)
    user.value = null
  }

  async function resendVerification(): Promise<void> {
    if (!user.value) return
    await sendEmailVerification(user.value)
  }

  async function refreshAccountStatus(): Promise<string> {
    if (!user.value) return ''
    await user.value.reload()
    await user.value.getIdToken(true)
    const callable = httpsCallable<Record<string, never>, { status: string }>(firebaseClient.functions, 'getAccountStatus')
    const result = await callable({})
    accountStatus.value = result.data.status
    return accountStatus.value
  }

  return {
    user,
    loading,
    initialized,
    error,
    accountStatus,
    isAuthenticated,
    displayName,
    initialize,
    register,
    login,
    continueWithGoogle,
    logout,
    resendVerification,
    refreshAccountStatus,
  }
})
